/**
 * Behavior Registry
 * 
 * Unified registry for all entity behaviors. Maps behavior IDs (e.g., 'cube', 'character:lilly')
 * to behavior definitions that include hooks and metadata.
 * 
 * Behaviors are composable definitions of what entities DO:
 * - Simple behaviors: 'physics', 'damageable', 'explosive'
 * - Complex presets: 'character:lilly', 'weapon:bass', 'npc:guard'
 * 
 * The colon convention is just namespacing - all are registered the same way.
 * 
 * @see src/core/behaviors/types.ts for the behavior type hierarchy
 * @see src/core/behaviors/compose.ts for composition utilities
 */

import { logger } from './Logger';
import type { EntityType } from '../types/EntityTypes';
import type { 
  CollisionHookAPI, 
  DamageHookAPI, 
  SpawnHookAPI, 
  DestroyHookAPI,
  BreakHookAPI
} from '../types/EntityTypes';
import type { DebrisParticleHook } from '../systems/particles/debrisEffects';
import type { BehaviorParameterSchema } from './BehaviorSchema';
import type { AbstractMesh, Scene, Vector3 } from '@babylonjs/core';
import type { FogConfig, LensConfig } from '../systems/LevelBuilder';
import type { PhysicsManager } from '../systems/physics/PhysicsManager';
import type { TriggerSystem } from '../systems/TriggerSystem';
import type { EnvironmentManager } from '../systems/EnvironmentManager';
import type { ParticleManager } from '../systems/ParticleManager';

// Re-export new behavior types for convenience
export type {
  Behavior,
  PhysicsBehavior,
  DamageableBehavior,
  TriggerBehavior,
  CharacterBehavior,
  WeaponBehavior,
  ProjectileBehavior,
} from './behaviors/types';

/**
 * Result from a damage hook - controls whether damage is modified or cancelled
 */
export interface DamageResult {
  /** Modified damage amount */
  damage: number;
  /** If true, damage is cancelled entirely */
  cancelled?: boolean;
}

/**
 * Context passed to behavior's onProcessNode hook during level loading
 */
export interface NodeProcessContext {
  /** The mesh/node being processed */
  mesh: AbstractMesh;
  
  /** Extracted extras (hm_* properties) */
  extras: Record<string, unknown>;
  
  /** Babylon scene */
  scene: Scene;
  
  /** Services available for delegation (some may be undefined) */
  services: {
    physics: PhysicsManager;
    triggers?: TriggerSystem;
    environment: EnvironmentManager;
    particles?: ParticleManager | null;
  };
  
  /** Accumulated results from level loading */
  results: NodeProcessResults;
  
  /** Helper to get behavior parameter with default */
  getParam: <T>(name: string, defaultValue?: T) => T;
}

/**
 * Results accumulated during level loading
 */
export interface NodeProcessResults {
  /** Player spawn position (set by spawn:player) */
  spawnPoint?: Vector3;
  
  /** Map configuration (set by config:scene) */
  mapConfig?: {
    physics: { gravity: number };
    ambient: Vector3;
    /** Maximum simultaneous lights (default 8) */
    maxLights?: number;
    /** Startup cinematic configuration */
    startup?: {
      cinematicId: string;
      events?: Array<{ time: number; hook: string; params?: Record<string, unknown> }>;
    };
    /** Name of camera to use for initial view */
    startCamera?: string;
    /** Global fog settings */
    fog?: FogConfig;
    /** Lens rendering pipeline settings */
    lens?: LensConfig;
  };
  
  /** Cull distance for spatial culling */
  cullDistance?: number;
  
  /** NPC spawn data (set by spawn:npc) */
  npcSpawns: Array<{
    id: string;
    characterId: string;
    position: Vector3;
    spawnRadius: number;
    patrolLeftDistance: number;
    patrolRightDistance: number;
    patrolWaitTime: number;
    maxHealth?: number;
    startHealth?: number;
    behaviorConfig?: Record<string, unknown>;
  }>;
}

/**
 * Result from behavior's onProcessNode
 */
export interface NodeProcessResult {
  /** Was this node fully handled by the behavior? */
  handled: boolean;
  
  /** Should physics be skipped for this node? */
  skipPhysics?: boolean;
  
  /** Should this node be hidden? */
  hideNode?: boolean;
  
  /** Should collision be disabled? */
  disableCollision?: boolean;
}

/**
 * Unified behavior definition for all entity types
 */
export interface EntityBehavior {
  /** Unique behavior ID (e.g., 'cube', 'daniel', 'fireball') */
  id: string;
  
  /** Display name for debug UI */
  displayName: string;
  
  /** What type of entity uses this behavior */
  type: EntityType;
  
  /** Category for organization */
  category?: 'character' | 'object' | 'projectile' | 'trigger' | 'debris' | 'fallback' | 'spawn' | 'config' | 'material' | 'emitter';
  
  /** Tags for filtering */
  tags?: string[];
  
  /** Description for Blender UI tooltips */
  description?: string;
  
  // === Schema for Blender Integration ===
  
  /** 
   * Parameter schema for this behavior.
   * Used to generate Blender addon UI and validate properties.
   */
  parameters?: BehaviorParameterSchema;
  
  // === Node Processing (for LevelBuilder delegation) ===
  
  /**
   * Called during level loading to process a node with this behavior.
   * Allows behaviors to handle their own processing logic instead of
   * putting it all in LevelBuilder.
   */
  onProcessNode?: (ctx: NodeProcessContext) => NodeProcessResult;
  
  // === State Machine ===
  
  /** Factory to create XState machine for this entity */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createMachine?: () => any;
  
  // === Lifecycle Hooks ===
  
  /** Called when entity is spawned */
  onSpawn?: (api: SpawnHookAPI) => void;
  
  /** Called when entity is destroyed */
  onDestroy?: (api: DestroyHookAPI) => void;
  
  /** Called when object transitions to breaking state */
  onBreak?: (api: BreakHookAPI) => void;
  
  // === Debris Configuration ===
  
  /** 
   * Hook called when debris hits the floor. Return particle config, or false to disable.
   * If not defined, uses default 'dust_puff' preset.
   * 
   * @example
   * // Use smoke factory with custom color
   * debrisParticle: () => smoke({ color: DustColors.wood })
   * 
   * @example
   * // Disable debris particles
   * debrisParticle: () => false
   */
  debrisParticle?: DebrisParticleHook;
  
  // === Collision Hooks ===
  
  /** Called on physics collision */
  onCollision?: (api: CollisionHookAPI) => void;
  
  // === Damage Hooks ===
  
  /** Called when receiving damage (can modify/cancel) */
  onDamage?: (api: DamageHookAPI) => DamageResult | void;
}

/**
 * Lookup result (simplified - no fallback)
 */
export interface BehaviorLookup {
  behavior: EntityBehavior;
  resolvedId: string;
}

/**
 * Central registry for entity behaviors
 * 
 * FAIL-FAST PHILOSOPHY:
 * - No fallback behaviors - if a behavior is missing, we throw
 * - No silent skipping - errors are visible and blocking
 * - All behaviors must be explicitly registered before use
 */
class BehaviorRegistryClass {
  private behaviors = new Map<string, EntityBehavior>();

  /**
   * Register a behavior
   */
  register(behavior: EntityBehavior): void {
    if (this.behaviors.has(behavior.id)) {
      logger.core.warn(`BehaviorRegistry: Overwriting existing behavior '${behavior.id}'`);
    }
    this.behaviors.set(behavior.id, behavior);
    logger.core.debug(`BehaviorRegistry: Registered '${behavior.id}' (${behavior.type})`);
  }

  /**
   * Register an alias for an existing behavior (for backwards compatibility)
   * 
   * @param alias - The old/legacy behavior ID
   * @param canonicalId - The new canonical behavior ID
   */
  alias(alias: string, canonicalId: string): void {
    const behavior = this.behaviors.get(canonicalId);
    if (!behavior) {
      throw new Error(`BehaviorRegistry: Cannot alias '${alias}' to unknown behavior '${canonicalId}'`);
    }
    if (this.behaviors.has(alias)) {
      logger.core.warn(`BehaviorRegistry: Overwriting existing behavior with alias '${alias}' -> '${canonicalId}'`);
    }
    this.behaviors.set(alias, behavior);
    logger.core.debug(`BehaviorRegistry: Aliased '${alias}' -> '${canonicalId}'`);
  }

  /**
   * Get behavior by ID (throws if not found)
   * 
   * @param behaviorId - The behavior ID to look up
   * @param context - Optional context for better error messages (e.g., node name)
   * @throws Error if behavior is not registered
   */
  get(behaviorId: string, context?: string): BehaviorLookup {
    // Map asset: prefix to object: (asset:cube -> object:cube)
    let lookupId = behaviorId;
    if (behaviorId.startsWith('asset:')) {
      lookupId = 'object:' + behaviorId.substring(6);
    }
    
    const behavior = this.behaviors.get(lookupId);

    if (!behavior) {
      const contextStr = context ? ` (on node '${context}')` : '';
      const available = Array.from(this.behaviors.keys()).sort().join(', ');
      throw new Error(
        `BehaviorRegistry: Unknown behavior '${behaviorId}' (looked up as '${lookupId}')${contextStr}. ` +
        `Did you mean one of: ${available.substring(0, 200)}...?`
      );
    }

    return {
      behavior,
      resolvedId: lookupId,
    };
  }

  /**
   * Get behavior directly without throwing (for optional lookups)
   */
  getDirect(behaviorId: string): EntityBehavior | undefined {
    return this.behaviors.get(behaviorId);
  }

  /**
   * Check if behavior is registered
   */
  has(behaviorId: string): boolean {
    return this.behaviors.has(behaviorId);
  }

  /**
   * Get all registered behavior IDs
   */
  getRegisteredIds(): string[] {
    return Array.from(this.behaviors.keys());
  }

  /**
   * Get behaviors by type
   */
  getByType(type: EntityType): EntityBehavior[] {
    return Array.from(this.behaviors.values()).filter(b => b.type === type);
  }

  /**
   * Clear all behaviors (for testing)
   */
  clear(): void {
    this.behaviors.clear();
  }
}

// Singleton instance
export const BehaviorRegistry = new BehaviorRegistryClass();
